import { normalizeCropCode } from "pages/admin/experiments/cropExtraction/cropCodes";
import {
  DbfRow,
  NamedDbfTable,
  findField,
  findTable,
  findYearInDbfRows,
  readAllDbfs,
  toNumber,
  toString,
} from "pages/admin/experiments/cropExtraction/parsers/genericDbf";
import { CropRecord, ParserResult } from "pages/admin/experiments/cropExtraction/types";

// iBALIS ships two coordinated DBFs: `Nutzung.dbf` (one row per crop /
// teil-schlag) and `Feldstueck.dbf` (the parent fields with names). We don't
// use `runDbfParser` here because we need to join across the two tables.

const buildFeldstueckIndex = (table: NamedDbfTable | undefined): Map<string, DbfRow> => {
  const index = new Map<string, DbfRow>();
  if (!table) return index;
  const idCol = findField(table.rows, ["FlNr", "FSNr", "FID", "FlaecheNr", "Schlag"]);
  if (!idCol) return index;
  for (const row of table.rows) {
    const key = toString(row[idCol]);
    if (key) index.set(key, row);
  }
  return index;
};

export const parseIBalis = async (zipBuffer: ArrayBuffer): Promise<ParserResult> => {
  const warnings: string[] = [];
  const tables = await readAllDbfs(zipBuffer);

  const nutzungTable = findTable(tables, (b) => b === "nutzung");
  if (!nutzungTable || nutzungTable.rows.length === 0) {
    return { method: "dbf_column", crops: [], warnings: ["Nutzung.dbf could not be read"] };
  }

  const cropCol = findField(nutzungTable.rows, ["Nutzung", "nutz_code", "kultur"]);
  const fieldIdCol = findField(nutzungTable.rows, ["Schlag", "FSNr", "FID", "FlNr"]);
  const fsnrCol = findField(nutzungTable.rows, ["FSNr", "FlNr"]);
  const areaCol = findField(nutzungTable.rows, ["Flaeche", "flaeche_ha", "groesse"]);

  const feldstueckTable = findTable(tables, (b) => b === "feldstueck");
  const feldstueckIndex = buildFeldstueckIndex(feldstueckTable);
  const fsNameCol = feldstueckTable ? findField(feldstueckTable.rows, ["Schlag", "Bezeichnung", "Name", "FB_BEZEICH"]) : undefined;

  const crops: CropRecord[] = [];
  for (const row of nutzungTable.rows) {
    const fieldId = fieldIdCol ? toString(row[fieldIdCol]) : "";
    const fsnr = fsnrCol ? toString(row[fsnrCol]) : "";

    let fieldName = fieldId;
    if (fsNameCol) {
      const fsRow = (fsnr && feldstueckIndex.get(fsnr)) || feldstueckIndex.get(fieldId);
      const candidate = fsRow ? toString(fsRow[fsNameCol]) : "";
      if (candidate) fieldName = candidate;
    }

    crops.push({
      fieldId: fieldId || fsnr,
      fieldName: fieldName || fieldId,
      cropCode: normalizeCropCode(cropCol ? toString(row[cropCol]) : ""),
      cropDescription: "",
      areaHa: areaCol ? toNumber(row[areaCol]) : 0,
      fieldNumber: fsnr || undefined,
    });
  }

  if (!feldstueckTable) {
    warnings.push("Feldstueck.dbf was missing — field names fall back to field IDs");
  }

  const year = findYearInDbfRows(nutzungTable) ?? findYearInDbfRows(feldstueckTable);
  return { method: feldstueckTable ? "xml_plus_dbf" : "dbf_column", crops, warnings, year };
};
